/**
 * Reset Password Page (Story 6-6-7)
 * Allows users to reset their password using a token from email.
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { authService } from "../../services/authService";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/atoms/Button";
import { Alert } from "../../components/molecules/Alert";
import { Card } from "../../components/molecules/Card";
import logoImage from "../../assets/global-clean-logo.png";
import { LanguageSwitcher } from "../../components/molecules/LanguageSwitcher";

interface ResetPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

export function ResetPassword() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();

  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect logged-in users to dashboard (AC 10)
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>();

  const newPassword = watch("newPassword");

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError(t("reset_no_token"));
        setIsValidating(false);
        return;
      }

      try {
        const result = await authService.validateResetToken(token);
        if (result.valid) {
          setTokenValid(true);
        } else {
          let errorMsg = t("reset_invalid_link");
          if (result.reason === "expired") {
            errorMsg = t("reset_expired_link");
          } else if (result.reason === "used") {
            errorMsg = t("reset_used_link");
          }
          setTokenError(errorMsg);
        }
      } catch (err) {
        setTokenError(t("reset_validate_failed"));
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError(t("reset_no_token"));
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      await authService.resetPassword(token, data.newPassword, data.confirmPassword);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("reset_failed");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading || (isAuthenticated && !isValidating)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-neutral-400">
            {authLoading ? t("reset_loading") : t("reset_redirecting")}
          </p>
        </div>
      </div>
    );
  }

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-neutral-400">{t("reset_validating")}</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900 px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="flex flex-col items-center">
            <img
              src={logoImage}
              alt={t("reset_logo_alt")}
              className="h-24 w-auto mb-4"
            />
          </div>

          <Card padding="lg">
            <Alert variant="danger">
              {tokenError}
            </Alert>
            <div className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t("reset_request_new_link")}
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900 px-4 relative">
      <div className="absolute top-4 end-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="max-w-md w-full space-y-8">
        <div className="flex flex-col items-center">
          <img
            src={logoImage}
            alt={t("reset_logo_alt")}
            className="h-24 w-auto mb-4"
          />
          <h2 className="mt-2 text-center text-2xl font-bold text-gray-900 dark:text-neutral-100">
            {t("reset_title")}
          </h2>
        </div>

        <Card padding="lg">
          {success ? (
            <div className="space-y-4">
              <Alert variant="success">
                {t("reset_success")}
              </Alert>
              <p className="text-center text-sm text-gray-600 dark:text-neutral-400">
                {t("reset_redirecting")}
              </p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <Alert variant="danger">
                  <div className="flex items-start justify-between gap-2">
                    <span>{error}</span>
                    <button
                      type="button"
                      onClick={() => setError("")}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex-shrink-0"
                      aria-label={t("reset_dismiss_error")}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </Alert>
              )}

              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
                >
                  {t("reset_new_password_label")}
                </label>
                <div className="relative mt-1">
                  <input
                    {...register("newPassword", {
                      required: t("reset_password_required"),
                      minLength: {
                        value: 10,
                        message: t("reset_password_min_length"),
                      },
                    })}
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    className="block w-full px-3 py-2 pe-10 border border-gray-300 dark:border-neutral-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 end-0 flex items-center pe-3 text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"
                    aria-label={showPassword ? t("reset_hide_password") : t("reset_show_password")}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.newPassword.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                  {t("reset_password_hint")}
                </p>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
                >
                  {t("reset_confirm_label")}
                </label>
                <div className="relative mt-1">
                  <input
                    {...register("confirmPassword", {
                      required: t("reset_confirm_required"),
                      validate: (value) =>
                        value === newPassword || t("reset_passwords_mismatch"),
                    })}
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    className="block w-full px-3 py-2 pe-10 border border-gray-300 dark:border-neutral-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 end-0 flex items-center pe-3 text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"
                    aria-label={showConfirmPassword ? t("reset_hide_password") : t("reset_show_password")}
                  >
                    {showConfirmPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading}>
                {isLoading ? t("reset_submitting") : t("reset_submit")}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t("reset_back_to_login")}
                </Link>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
